import { MedplumClient } from "@medplum/core";
import { CodeSystemConcept, ValueSetExpansionContains } from "@medplum/fhirtypes";

import { QUICK_PICKER_PROPERTY } from "@/encounters/constants/procedures";

/** A quick picker code with the fee schedule's Info box text. */
export type QuickPickerCoding = ValueSetExpansionContains & { information?: string };

/** Whether a fee schedule concept is flagged for the CPT quick picker. */
export const isOnQuickPicker = (concept: CodeSystemConcept): boolean =>
  (concept.property ?? []).some(
    (property) => property.code === QUICK_PICKER_PROPERTY && property.valueBoolean === true,
  );

/**
 * Reads one fee schedule CodeSystem and returns its quick picker codes in fee schedule order, with
 * the same name and Info box text the Fee Table shows.
 *
 * @param medplum The signed-in Medplum client.
 * @param codeSystemUrl The fee schedule CodeSystem behind one quick picker section.
 * @returns The flagged codes, or an empty list when the CodeSystem is not seeded.
 */
export const loadQuickPickerCodings = async (
  medplum: MedplumClient,
  codeSystemUrl: string,
): Promise<QuickPickerCoding[]> => {
  const codeSystem = await medplum.searchOne("CodeSystem", {
    url: codeSystemUrl,
    status: "active",
  });

  return (codeSystem?.concept ?? []).reduce<QuickPickerCoding[]>((codings, concept) => {
    if (isOnQuickPicker(concept)) {
      codings.push({
        system: codeSystemUrl,
        code: concept.code,
        display: concept.display,
        information: concept.definition,
      });
    }
    return codings;
  }, []);
};
